import { message, Spin } from 'antd'
import { useRolodexEdit } from 'features/fde'
import * as React from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryPatchJson } from 'types'

import { DocumentList } from './components/DocumentList'
import { DocumentPreview } from './components/DocumentPreview'
import { DocumentTagging } from './components/DocumentTaggingForm'
import { useLinkedDocumentsForEntry } from './hooks/useDocuments'
import { Document, DocumentTag } from './types'

export interface DocumentComponentProps {
  entry: GeckoEntry
  className?: string
  onComplete?: (documentIds: ReadonlyArray<string>) => void
  onError?: (error: string) => void
  defaultKeys?: string[]
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.9375rem;
  width: 100%;
  align-items: flex-start;
`

const FileListSection = styled.div`
  flex: 0 0 auto;
  min-width: 220px;
  max-width: 320px;
`

const TagEditorSection = styled.div`
  flex: 1 1 0;
  min-width: 400px;
`

const PreviewSection = styled.div`
  flex: 2 1 0;
  min-width: 600px;
  min-height: 500px;
`

/**
 * Build initial document fields object with existing field data and any default fields (set to empty strings)
 */
const buildInitialDocumentFields = (
  documents: ReadonlyArray<Document>,
  defaultKeys: ReadonlyArray<string> = []
): Record<string, DocumentTag[]> => {
  const fields: Record<string, DocumentTag[]> = {}

  documents.forEach((doc) => {
    const existingFields: DocumentTag[] = Object.entries(doc.fields).map(
      ([key, value]) => ({ key, value: String(value) })
    )

    const existingKeys = new Set(existingFields.map((field) => field.key))
    const defaultFields: DocumentTag[] = defaultKeys
      .filter((key) => !existingKeys.has(key))
      .map((key) => ({ key, value: '' }))

    fields[doc.entryId] = [...existingFields, ...defaultFields]
  })

  return fields
}

/**
 * Convert UI fields to API format
 */
const convertFieldsToApiFormat = (
  fields: ReadonlyArray<DocumentTag>
): Record<string, string> => {
  const apiFields: Record<string, string> = {}
  fields
    .filter((field) => field.key.trim())
    .forEach((field) => {
      apiFields[field.key.trim()] = field.value.trim()
    })
  return apiFields
}

/**
 * A component which assists with tagging fields on documents.
 * @param entry - The entry, the documents of which will be tagged.
 * @param defaultKeys - The default keys to use for the tagging, to be initialised to empty strings if not present.
 */
export const DocumentTagger: React.FC<DocumentComponentProps> = ({
  entry,
  onComplete,
  onError,
  className,
  defaultKeys,
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<
    string | null
  >(null)
  const [documentFields, setDocumentFields] = React.useState<
    Readonly<Record<string, DocumentTag[]>>
  >({})
  const [isSaving, setIsSaving] = React.useState(false)

  const { rolodexPatch } = useRolodexEdit()
  const { documents, isLoading: areDocumentsLoading } =
    useLinkedDocumentsForEntry(entry)

  React.useEffect(() => {
    if (documents.length === 0) return

    setSelectedDocumentId((prev) =>
      !prev || !documents.find((doc) => doc.entryId === prev)
        ? documents[0].entryId
        : prev
    )

    const newFields = buildInitialDocumentFields(documents, defaultKeys || [])
    setDocumentFields({ ...newFields })
  }, [documents, defaultKeys])

  const selectedDocument = useMemo(
    () => documents.find((doc) => doc.entryId === selectedDocumentId),
    [documents, selectedDocumentId]
  )
  const selectedFields = useMemo(
    () => documentFields[selectedDocumentId || ''] || [],
    [documentFields, selectedDocumentId]
  )

  const handleFieldsChange = useCallback(
    (fields: DocumentTag[]) => {
      if (!selectedDocumentId) return
      setDocumentFields((prev) => ({
        ...prev,
        [selectedDocumentId]: fields,
      }))
    },
    [selectedDocumentId]
  )

  const handleSaveFields = useCallback(async () => {
    if (!selectedDocumentId || !selectedDocument) return
    setIsSaving(true)

    try {
      const validFields = selectedFields.filter(
        (field) => field.key.trim() !== '' && field.value.trim() !== ''
      )

      if (validFields.length === 0) {
        message.warning('Please add at least one field with both key and value')
        return
      }

      const patchBody: RolodexEntryPatchJson = {
        id: selectedDocumentId,
        data: { fields: convertFieldsToApiFormat(validFields) },
      }

      await rolodexPatch([patchBody])
      message.success(`Fields saved for ${selectedDocument.name}`)

      setDocumentFields((prev) => ({
        ...prev,
        [selectedDocumentId]: validFields,
      }))

      onComplete?.([selectedDocumentId])
    } catch (error) {
      const errorMsg = `Failed to save fields for ${selectedDocument.name}: ${String(error)}`
      message.error(errorMsg)
      onError?.(errorMsg)
    } finally {
      setIsSaving(false)
    }
  }, [
    selectedDocumentId,
    selectedDocument,
    selectedFields,
    rolodexPatch,
    onComplete,
    onError,
  ])

  if (areDocumentsLoading) {
    return (
      <Container className={className}>
        <FileListSection>
          <Spin spinning />
        </FileListSection>
        <TagEditorSection />
        <PreviewSection />
      </Container>
    )
  }

  return (
    <Container className={className}>
      <FileListSection>
        <DocumentList
          documents={documents}
          selectedDocumentId={selectedDocumentId}
          onDocumentSelect={setSelectedDocumentId}
        />
      </FileListSection>
      <PreviewSection>
        <DocumentPreview document={selectedDocument || null} />
      </PreviewSection>
      <TagEditorSection>
        <DocumentTagging
          document={selectedDocument || null}
          fields={selectedFields}
          onFieldsChange={handleFieldsChange}
          onSave={handleSaveFields}
          saving={isSaving}
          defaultKeys={defaultKeys}
        />
      </TagEditorSection>
    </Container>
  )
}
